import React from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { Divider, useAlert } from 'fasoo-ui-component-next';
import _ from 'lodash';

import approvalDetailApi, {
  ApprovalDetailEntity,
  ApprovalStatusType,
  RequestApprovalDto,
} from 'src/api/user/approvalApi';
import { PrintPeriodApprovalDetailEntity } from 'src/api/user/printPeriodApprovalApi';
import { CommonProps } from 'src/components/helpers/props';
import usePopup from 'src/hooks/usePopup';
import { getFasooUiComponentLanguage } from 'src/i18n';
import { toLocalDateTimeLangStr } from 'src/shared/utils/localDateTime';

import { ApprovalDetailFooter } from '../ApprovalDetailFooter';

import { ApprovalTypeIconArea } from './ApprovalTypeIconArea';
import RemarkPopup from './RemarkPopup';
import { ApprovalUserInfoDto, ShowLineContent, UserDetail } from '.';

import './PrintPeriodPolicyDetail.scss';

interface PrintPeriodPolicyApprovalDetailProps extends CommonProps {
  printperiodpolicyApprovalDetail: PrintPeriodApprovalDetailEntity;
  ResetDetail: () => void;
  cancelApprovalChildren?: React.ReactNode;
  onCloseDetail: (open: boolean, isReject?: boolean) => void;
  isComplete?: boolean;
  selectedApproval?: ApprovalDetailEntity | null;
}

const PrintPeriodPolicyDetail: React.FC<PrintPeriodPolicyApprovalDetailProps> = ({
  printperiodpolicyApprovalDetail,
  ResetDetail,
  cancelApprovalChildren,
  onCloseDetail,
  isComplete = false,
  selectedApproval,
}) => {
  const { t } = useTranslation(['menu', 'userApproval']);
  const { alert, confirm } = useAlert();
  const openApprovalPopup = usePopup();
  const openRejectPopup = usePopup();

  let approvers: ApprovalUserInfoDto[] = [];
  if (printperiodpolicyApprovalDetail.completeApprovers.length > 0) {
    const completes = _.sortBy(printperiodpolicyApprovalDetail.completeApprovers, 'step');
    approvers = approvers.concat(
      completes.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: approver.approvalStatus,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
          actorIp: approver.actorIp,
          approvalRemark: approver.approvalRemark,
          approvalDate: approver.updatedTs,
          method: approver.approvalMethod,
          otherDeptCode: approver.otherDeptCode === null ? null : approver.otherDeptCode,
          otherDeptName: approver.otherDeptName === null ? null : approver.otherDeptName,
          otherPositionCode:
            approver.otherPositionCode === null ? null : approver.otherPositionCode,
          otherPositionName:
            approver.otherPositionName === null ? null : approver.otherPositionName,
          otherRoleCode: approver.otherRoleCode === null ? null : approver.otherRoleCode,
          otherRoleName: approver.otherRoleName === null ? null : approver.otherRoleName,
          otherUserId: approver.otherUserId === null ? null : approver.otherUserId,
          otherUserName: approver.otherUserName === null ? null : approver.otherUserName,
        };
      })
    );
  }
  if (printperiodpolicyApprovalDetail.progressApprovers.length > 0) {
    const progresses = _.sortBy(printperiodpolicyApprovalDetail.progressApprovers, 'step');
    approvers = approvers.concat(
      progresses.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
    );
  }
  // 결재선이 다 끝나지 않는 단계
  // if (printperiodpolicyApprovalDetail.currentStep < printperiodpolicyApprovalDetail.approvalStep) {
  //   let postApprovalSteps: number[] = [];
  //   for (
  //     let i = printperiodpolicyApprovalDetail.currentStep + 1;
  //     i <= printperiodpolicyApprovalDetail.approvalStep;
  //     i++
  //   ) {
  //     postApprovalSteps = postApprovalSteps.concat(i);
  //   }

  approvers = approvers.concat(
    printperiodpolicyApprovalDetail.approvers
      .filter(
        (approver) =>
          // postApprovalSteps.includes(approver.step) &&
          approvers.filter((v) => v.step === approver.step)?.length === 0
      )
      .map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
  );
  // }

  const handlePrintPrintRange = (): string => {
    const tempPrintRange: string[] = [];
    if (printperiodpolicyApprovalDetail.watermarkPrint === 'Y') {
      tempPrintRange.push(t('userApproval:watermarkPrint'));
    }
    if (printperiodpolicyApprovalDetail.detectionPrint === 'Y') {
      tempPrintRange.push(t('userApproval:detectionPrint'));
    }
    return tempPrintRange.map((item) => item).join(' · ');
  };

  const handleOpenApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.openPopup();
    } else if (type === 'reject') {
      openRejectPopup.openPopup();
    }
  };

  const handleCloseApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.closePopup();
    } else if (type === 'reject') {
      openRejectPopup.closePopup();
    }
  };

  const handleApprovalReject = (remark: string): void => {
    const popupType = openApprovalPopup.opened !== false ? 'approval' : 'reject';

    // console.log('얘가 작성한 remark : ', remark);
    const progressApproverInfo = printperiodpolicyApprovalDetail.progressApprovers[0];
    const reqApprovalDto: RequestApprovalDto = {
      userId: progressApproverInfo.userId,
      approvalNo: printperiodpolicyApprovalDetail.approvalNo,
      approvalMethod: printperiodpolicyApprovalDetail.approvalMethod,
      approvalType: printperiodpolicyApprovalDetail.approvalType,
      status: popupType === 'approval' ? 'APPROVAL' : 'REJECT',
      remark,
      proxyNo: selectedApproval?.progressApprovers[0].proxyNo
        ? selectedApproval.progressApprovers[0].proxyNo
        : null,
    };
    approvalDetailApi
      .approvalOrReject(reqApprovalDto)
      .then((resp) => {
        onCloseDetail(false, true);
      })
      .catch((err) => alert(err, 'error', '', undefined, true));
    handleCloseApprovalPopup(popupType);
  };

  return (
    <div className="printperiodpolicy-detail-root">
      <div
        className={classNames('printperiodpolicy-detail-maincontainer', {
          'mg mb-85': isComplete === false,
          // 'mg mb-32': isComplete === true,
        })}
      >
        {/* 신청 유형 아이콘 area */}

        <ApprovalTypeIconArea
          onClose={onCloseDetail}
          type={printperiodpolicyApprovalDetail.approvalType}
          status={printperiodpolicyApprovalDetail.status}
        />

        <div className="detail-body">
          <ShowLineContent
            classes="mg mt-32"
            title={t('userApproval:rightPeriod')}
            content={
              printperiodpolicyApprovalDetail.startDate < 1
                ? t('commons:unlimited')
                : `${toLocalDateTimeLangStr(
                    printperiodpolicyApprovalDetail.startDate,
                    getFasooUiComponentLanguage()
                  )} ~ ${toLocalDateTimeLangStr(
                    printperiodpolicyApprovalDetail.endDate,
                    getFasooUiComponentLanguage()
                  )}`
            }
          />

          {!printperiodpolicyApprovalDetail.approvalMethod || (
            <ShowLineContent
              classes={classNames('approval-method', 'mg mt-32')}
              title={t('userApproval:approvalMethod.$')}
              content={t(
                `userApproval:approvalMethod.${printperiodpolicyApprovalDetail.approvalMethod}`
              )}
            />
          )}

          <ShowLineContent
            classes="mg mt-32"
            title={t(`userApproval:exRange`)}
            content={handlePrintPrintRange()}
          />
          {printperiodpolicyApprovalDetail.watermarkPrint === 'Y' &&
            (printperiodpolicyApprovalDetail.norDocPrint !== null ||
              printperiodpolicyApprovalDetail.secDocPrint !== null) && (
              <ShowLineContent
                classes="mg mt-16"
                title={t(`userApproval:printType.title`)}
                content={
                  <>
                    {/* 문서별 출력 리소스 대기 */}
                    {printperiodpolicyApprovalDetail.norDocPrint !== null && (
                      <ShowLineContent
                        subTitle={t(`userApproval:printType.normalDoc`)}
                        subTitleSize="sm"
                        content={t(
                          `userApproval:printType.${printperiodpolicyApprovalDetail.norDocPrint}`
                        )}
                      />
                    )}
                    {printperiodpolicyApprovalDetail.secDocPrint !== null && (
                      <ShowLineContent
                        subTitle={t(`userApproval:printType.secDoc`)}
                        subTitleSize="sm"
                        content={t(
                          `userApproval:printType.${printperiodpolicyApprovalDetail.secDocPrint}`
                        )}
                      />
                    )}
                  </>
                }
              />
            )}
          <ShowLineContent
            classes="mg mt-32"
            title={t('userApproval:applicant')}
            content={
              <UserDetail
                type="request"
                dateStr={toLocalDateTimeLangStr(
                  printperiodpolicyApprovalDetail.applyDate,
                  getFasooUiComponentLanguage()
                )}
                remark={printperiodpolicyApprovalDetail.remark}
                userInfo={{
                  userId: printperiodpolicyApprovalDetail.userId,
                  userName: printperiodpolicyApprovalDetail.userName,
                  positionName: printperiodpolicyApprovalDetail.positionName,
                  deptName: printperiodpolicyApprovalDetail.deptName,
                  actorIp: printperiodpolicyApprovalDetail.actorIp,
                }}
              />
            }
          />
        </div>
        <div className="approval-detail-info-title">
          <div className="title-text">{t('userApproval:approvalDetail.$')}</div>
        </div>
        <div className="detail-body">
          <ShowLineContent
            title=""
            content={
              <>
                {approvers.map((approver, idx) => {
                  return (
                    <UserDetail
                      step={approvers.length > 1 ? approver.step : undefined}
                      type="approver"
                      dateStr={
                        approver.approvalDate
                          ? toLocalDateTimeLangStr(
                              approver.approvalDate,
                              getFasooUiComponentLanguage()
                            )
                          : null
                      }
                      remark={approver.approvalRemark ? approver.approvalRemark : null}
                      userInfo={approver}
                      key={`approver-${idx}`}
                    />
                  );
                })}

                {approvers.length <= 1 || <Divider />}
              </>
            }
          />

          {cancelApprovalChildren}
        </div>
      </div>
      {isComplete === false && (
        <ApprovalDetailFooter
          approvalMethod={printperiodpolicyApprovalDetail.approvalMethod}
          handleApprove={() => handleOpenApprovalPopup('approval')}
          handleReject={() => handleOpenApprovalPopup('reject')}
        />
      )}
      {(openApprovalPopup.opened === true || openRejectPopup.opened === true) && (
        <RemarkPopup
          open={
            openApprovalPopup.opened === true ? openApprovalPopup.opened : openRejectPopup.opened
          }
          entity={printperiodpolicyApprovalDetail}
          onSubmit={handleApprovalReject}
          onClose={handleCloseApprovalPopup}
          isReject={openApprovalPopup.opened !== true}
        />
      )}
    </div>
  );
};

export default PrintPeriodPolicyDetail;
